import { useState } from "react";
import { motion } from "framer-motion";
import {
  Upload,
  FileCheck,
  FileX,
  Clock,
  AlertCircle,
  CheckCircle2,
  FileText,
  Eye
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPES = [
  { key: "diploma", label: "Diplôme sportif", description: "BPJEPS, DEJEPS, CQP, STAPS..." },
  { key: "carte_pro", label: "Carte professionnelle", description: "Carte délivrée par la DDCS" },
  { key: "rcpro", label: "Attestation RC Pro", description: "Responsabilité civile professionnelle" }
];

const STATUS_BADGES = {
  pending: { label: "En cours de vérification", color: "bg-yellow-500/20 text-yellow-500", icon: Clock },
  validated: { label: "Validé", color: "bg-green-500/20 text-green-500", icon: FileCheck },
  rejected: { label: "Refusé", color: "bg-red-500/20 text-red-500", icon: FileX }
};

const CoachDocuments = ({ coach, onUpdated }) => {
  const [uploading, setUploading] = useState(null);
  const [rcproNumber, setRcproNumber] = useState(coach?.rcpro_number || "");
  const [savingRcpro, setSavingRcpro] = useState(false);

  const documents = coach?.documents || {};
  const allValidated = coach?.documents_validated;

  const handleUpload = async (docType, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10MB)");
      return;
    }
    setUploading(docType);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await axios.post(`${API}/coach-auth/documents/${docType}`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Document envoyé ! L'admin va le vérifier.");
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setUploading(null);
    }
  };

  const handleSaveRcpro = async () => {
    setSavingRcpro(true);
    try {
      await axios.put(`${API}/coach-auth/profile`, { rcpro_number: rcproNumber }, { withCredentials: true });
      toast.success("Numéro RC Pro enregistré");
      onUpdated();
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSavingRcpro(false);
    }
  };

  const handlePreview = async (docType) => {
    try {
      const response = await axios.get(`${API}/coach-auth/documents/${docType}/file`, {
        withCredentials: true,
        responseType: "blob"
      });
      const blobUrl = URL.createObjectURL(response.data);
      window.open(blobUrl, "_blank");
    } catch (err) {
      toast.error("Impossible d'ouvrir le document");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
      data-testid="coach-documents"
    >
      {/* Overall validation banner */}
      <div className={`p-5 border ${allValidated ? "bg-green-500/10 border-green-500/30" : "bg-yellow-500/10 border-yellow-500/30"}`}>
        <div className="flex items-start gap-3">
          {allValidated ? (
            <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          )}
          <div>
            <p className={`font-semibold ${allValidated ? "text-green-500" : "text-yellow-500"}`}>
              {allValidated ? "Tous vos documents sont validés" : "Vérification en cours"}
            </p>
            <p className="text-[#94A3B8] text-sm mt-1 leading-relaxed">
              {allValidated
                ? "Vous avez accès à toutes les fonctionnalités de la plateforme."
                : "Déposez les 3 documents requis. Une fois validés par l'admin, vous accéderez à toutes les fonctionnalités."}
            </p>
          </div>
        </div>
      </div>

      {/* RC Pro Number */}
      <div className="bg-[#0F0F16] border border-white/10 p-6">
        <h3 className="font-['Outfit'] text-lg font-bold uppercase mb-1">Numéro RC Pro</h3>
        <p className="text-[#94A3B8] text-sm mb-4">Numéro de votre assurance responsabilité civile professionnelle (visible sur votre profil public)</p>
        <div className="flex gap-2">
          <Input
            value={rcproNumber}
            onChange={(e) => setRcproNumber(e.target.value)}
            placeholder="Ex: 123456789"
            className="bg-[#05050A] border-white/10 flex-1"
            data-testid="rcpro-number-input"
          />
          <button
            onClick={handleSaveRcpro}
            disabled={savingRcpro || rcproNumber === (coach?.rcpro_number || "")}
            className="btn-primary px-6 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="save-rcpro-button"
          >
            {savingRcpro ? "..." : "Enregistrer"}
          </button>
        </div>
      </div>

      {/* Document Upload Slots */}
      <div className="space-y-4">
        {DOC_TYPES.map((dt) => {
          const doc = documents[dt.key];
          const badge = doc ? STATUS_BADGES[doc.status] : null;
          const Icon = badge?.icon || Upload;
          return (
            <div key={dt.key} className="bg-[#0F0F16] border border-white/10 p-6" data-testid={`doc-slot-${dt.key}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <FileText className="w-5 h-5 text-[#0066FF] flex-shrink-0" />
                    <h4 className="font-['Outfit'] font-bold text-base uppercase">{dt.label}</h4>
                    {badge && (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold ${badge.color}`}>
                        <Icon className="w-3 h-3" />
                        {badge.label}
                      </span>
                    )}
                  </div>
                  <p className="text-[#94A3B8] text-sm mb-3">{dt.description}</p>
                  {doc && (
                    <p className="text-[#64748B] text-xs">
                      {doc.original_filename} · {(doc.size / 1024).toFixed(0)} KB
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {doc && (
                    <button
                      onClick={() => handlePreview(dt.key)}
                      className="p-2 border border-white/10 hover:border-[#0066FF] hover:bg-[#0066FF]/10 transition-colors"
                      title="Aperçu"
                      data-testid={`preview-${dt.key}`}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  )}
                  <Label className="btn-primary cursor-pointer flex items-center gap-2 py-2 px-4 text-sm" data-testid={`upload-${dt.key}`}>
                    <Upload className="w-4 h-4" />
                    {uploading === dt.key ? "Envoi..." : doc ? "Remplacer" : "Déposer"}
                    <input
                      type="file"
                      accept=".pdf,image/png,image/jpeg"
                      className="hidden"
                      disabled={uploading === dt.key}
                      onChange={(e) => handleUpload(dt.key, e.target.files?.[0])}
                    />
                  </Label>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-center text-xs text-[#64748B]">
        Formats acceptés: PDF, PNG, JPG · Taille max: 10MB
      </p>
    </motion.div>
  );
};

export default CoachDocuments;
